import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import api from "../../service/api";
import AssignmentAreaSelects, { telecallerAreaDisplay } from '../../components/AssignmentAreaSelects';
import { useToast } from '../../contexts/ToastContext';
import './UserDetails.css';

const DetailItem = ({ icon, tone, label, value, valueClassName = '' }) => (
  <div className="ud-detail-item">
    <div className={`ud-detail-icon ud-detail-icon-${tone}`} aria-hidden="true">
      <i className={`bi ${icon}`} />
    </div>
    <div className="ud-detail-copy">
      <div className="ud-detail-label">{label}</div>
      <div className={`ud-detail-value ${valueClassName}`}>{value}</div>
    </div>
  </div>
);

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: 'telecaller',
    assignedWard: '',
    assignedWardNo: '',
    assignedBooth: '',
    isActive: true,
    profile: {
      firstName: '',
      lastName: '',
      phone: ''
    },
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      const res = await api.get(`/api/admin/users/${id}`);
      setUser(res.data);
      setFormData({
        username: res.data.username,
        email: res.data.email,
        role: res.data.role,
        assignedWard: res.data.assignedWard || '',
        assignedWardNo: res.data.assignedWardNo || '',
        assignedBooth: res.data.assignedBooth || '',
        isActive: res.data.isActive,
        profile: {
          firstName: res.data.profile?.firstName || '',
          lastName: res.data.profile?.lastName || '',
          phone: res.data.profile?.phone || ''
        },
        password: '',
        confirmPassword: ''
      });
    } catch (error) {
      showError('Failed to fetch user details');
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      const password = updateData.password?.trim();
      const confirmPassword = updateData.confirmPassword?.trim();

      if (password || confirmPassword) {
        if (!password) {
          showError('Please enter a new password first.');
          return;
        }
        if (!confirmPassword) {
          showError('Please confirm the new password.');
          return;
        }
        if (password.length < 6 || password.length > 25) {
          showError('Password must be between 6 and 25 characters long.');
          return;
        }
        if (password !== confirmPassword) {
          showError('Password and confirm password must match.');
          return;
        }
      }

      if (!password) {
        delete updateData.password;
      }

      await api.put(`/api/admin/users/${id}`, updateData);

      showSuccess('User updated successfully');
      setEditing(false);
      fetchUserDetails();
    } catch (error) {
      showError('Failed to update user');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('profile.')) {
      const profileField = name.split('.')[1];
      setFormData({
        ...formData,
        profile: {
          ...formData.profile,
          [profileField]: value
        }
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-50 py-5 mt-5">
        <Spinner animation="border" variant="primary" size="xl" />
        <p className="mt-3 text-secondary ls-wide small">FETCHING USER DETAILS...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <Container className="py-12 mt-5 text-center">
        <Card className="border-0 shadow-sm rounded-4 p-5">
          <div className="bg-light rounded-circle d-inline-flex p-4 mb-3 mx-auto">
            <i className="bi bi-person-x fs-1 text-danger"></i>
          </div>
          <h2 className="fw-bold text-dark">User Not Found</h2>
          <p className="text-secondary mb-4">The user you are looking for might have been deleted or moved.</p>
          <Button
            onClick={() => navigate('/admin/users')}
            className="rounded-pill px-4 py-2.5 mx-auto"
            variant="primary"
          >
            <i className="bi bi-arrow-left me-2"></i>
          </Button>
        </Card>
      </Container>
    );
  }

  const displayName = [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ') || user.username;
  const initials = `${(user.profile?.firstName?.[0] || user.username?.[0] || '').toUpperCase()}${(user.profile?.lastName?.[0] || '').toUpperCase()}`;
  const roleLabel = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '—';
  const area = telecallerAreaDisplay(user);
  const unspecified = <span className="ud-muted">Not specified</span>;

  return (
    <div className="ud-page">
      <Container fluid className="ud-container">
        <div className="ud-nav">
          <Button
            variant="light"
            className="ud-pill-button ud-back-button"
            onClick={() => navigate('/admin/users')}
          >
            <i className="bi bi-arrow-left"></i>
            Back to User Management
          </Button>
          <div className="ud-breadcrumb">
            <span>ADMIN PANEL</span>
            <i className="bi bi-chevron-right"></i>
            <span className="is-current">USER DETAILS</span>
          </div>
        </div>

        <Card className="ud-card ud-header-card">
          <Card.Body>
            <div className="ud-header">
              <div className="ud-header-identity">
                <div className="ud-avatar" aria-hidden="true">{initials}</div>
                <div>
                  <h1 className="ud-name">{displayName}</h1>
                  <div className="ud-header-meta">
                    <span className="ud-user-id">ID: {user._id.slice(-6).toUpperCase()}</span>
                    <span className={`ud-chip ${user.isActive ? 'is-active' : 'is-inactive'}`}>
                      {user.isActive ? 'Active Member' : 'Inactive Account'}
                    </span>
                    <span className="ud-chip is-role">{roleLabel}</span>
                  </div>
                </div>
              </div>
              {!editing && (
                <Button
                  variant="primary"
                  className="ud-pill-button ud-edit-button"
                  onClick={() => setEditing(true)}
                >
                  <i className="bi bi-pencil-square"></i>
                  Edit User Information
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>

        <Card className="ud-card ud-details-card">
          <Card.Body>
            {editing ? (
              <Form onSubmit={handleSubmit} className="ud-edit-form">
                <section className="ud-form-section">
                  <h2 className="ud-section-title">
                    <span className="ud-detail-icon ud-detail-icon-blue"><i className="bi bi-person"></i></span>
                    Account Credentials
                  </h2>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="ud-field-label">
                          <span className="ud-detail-icon ud-detail-icon-blue"><i className="bi bi-person"></i></span>
                          Username
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          required
                          className="ud-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="ud-field-label">
                          <span className="ud-detail-icon ud-detail-icon-blue"><i className="bi bi-envelope"></i></span>
                          Email
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="ud-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="ud-field-label">
                          <span className="ud-detail-icon ud-detail-icon-purple"><i className="bi bi-shield"></i></span>
                          Privilege Role
                        </Form.Label>
                        <Form.Select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          className="ud-input"
                        >
                          <option value="telecaller">Telecaller</option>
                          <option value="admin">Admin</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </section>

                {formData.role === 'telecaller' && (
                  <section className="ud-panel ud-panel-area">
                    <div className="ud-panel-heading">
                      <span className="ud-detail-icon ud-detail-icon-purple"><i className="bi bi-map"></i></span>
                      <div>
                        <h3>Call Area</h3>
                        <p>Assembly – that assembly, all wards, all booths. A specific booth stores only the booth.</p>
                      </div>
                    </div>
                    <AssignmentAreaSelects
                      grouped={false}
                      value={formData}
                      onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
                      selectClassName="ud-input"
                      labelClassName="ud-field-label-plain"
                    />
                  </section>
                )}

                <section className="ud-panel ud-panel-password">
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="ud-field-label">
                          <span className="ud-detail-icon ud-detail-icon-green"><i className="bi bi-key"></i></span>
                          Change Password
                        </Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter new password to reset (leave blank to keep current)"
                          className="ud-input"
                          autoComplete="new-password"
                        />
                        <Form.Text className="ud-help-text">
                          Use a strong password with at least 8 characters.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="ud-field-label">
                          <span className="ud-detail-icon ud-detail-icon-green"><i className="bi bi-key"></i></span>
                          Confirm Password
                        </Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Re-enter new password to confirm"
                          className="ud-input"
                          autoComplete="new-password"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </section>

                <section className="ud-panel ud-panel-profile">
                  <h2 className="ud-section-title">
                    <span className="ud-detail-icon ud-detail-icon-orange"><i className="bi bi-card-text"></i></span>
                    Personal Profile Info
                  </h2>
                  <Row className="g-2">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="ud-field-label-plain">First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="profile.firstName"
                          value={formData.profile.firstName}
                          onChange={handleChange}
                          className="ud-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="ud-field-label-plain">Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="profile.lastName"
                          value={formData.profile.lastName}
                          onChange={handleChange}
                          className="ud-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="ud-field-label-plain">Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          name="profile.phone"
                          value={formData.profile.phone}
                          onChange={handleChange}
                          className="ud-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </section>

                <div className="ud-form-actions">
                  <Button
                    type="button"
                    variant="light"
                    className="ud-pill-button ud-back-button"
                    onClick={() => setEditing(false)}
                  >
                    Discard Changes
                  </Button>
                  <Button type="submit" variant="primary" className="ud-pill-button ud-edit-button">
                    <i className="bi bi-cloud-arrow-up"></i>
                    Update Record
                  </Button>
                </div>
              </Form>
            ) : (
              <>
                <h2 className="ud-section-title">
                  <i className="bi bi-person"></i>
                  Account Information
                </h2>
                <div className="ud-detail-grid">
                  <DetailItem icon="bi-person" tone="blue" label="Username" value={user.username} />
                  <DetailItem icon="bi-envelope" tone="blue" label="Email" value={user.email} />
                  <DetailItem icon="bi-shield" tone="blue" label="Role" value={roleLabel} />
                  <DetailItem
                    icon={user.isActive ? 'bi-check-circle' : 'bi-x-circle'}
                    tone={user.isActive ? 'green' : 'red'}
                    label="Status"
                    value={user.isActive ? 'Active' : 'Inactive'}
                    valueClassName={user.isActive ? 'is-active' : 'is-inactive'}
                  />
                  <DetailItem icon="bi-building" tone="blue" label="Assigned Assembly" value={area.assembly} />
                  <DetailItem icon="bi-map" tone="purple" label="Assigned Ward" value={area.ward} />
                  <DetailItem icon="bi-shop" tone="purple" label="Assigned Booth" value={area.booth} />
                </div>

                <hr className="ud-divider" />

                <h2 className="ud-section-title">
                  <i className="bi bi-card-text"></i>
                  Profile Details
                </h2>
                <div className="ud-detail-grid">
                  <DetailItem icon="bi-person" tone="blue" label="First Name" value={user.profile?.firstName || unspecified} />
                  <DetailItem icon="bi-person" tone="blue" label="Last Name" value={user.profile?.lastName || unspecified} />
                  <DetailItem icon="bi-telephone" tone="blue" label="Phone" value={user.profile?.phone || unspecified} />
                  <DetailItem
                    icon="bi-calendar"
                    tone="blue"
                    label="Joined Date"
                    value={user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString(undefined, { dateStyle: 'long' })
                      : unspecified}
                  />
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default UserDetails;
